import { FileIcon, X as XIcon } from "lucide-react";
import { type KeyboardEvent, type Ref, useImperativeHandle, useRef, useState } from "react";

import { cn } from "@/lib/utils";

export interface DragDropTextareaHandle {
  focus: () => void;
  triggerFileUpload: () => void;
}

interface DragDropTextareaProps {
  value: string;
  onValueChange: (value: string) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  acceptedFileTypes?: string;
  onSubmit?: () => void;
  onFileClick?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  ref?: Ref<DragDropTextareaHandle>;
}

/** Port of ui/textarea/DragDropTextarea.vue — textarea with drag-drop and paste file support. */
export function DragDropTextarea({
  value,
  onValueChange,
  file,
  onFileChange,
  placeholder = "Entrez votre texte...",
  rows = 3,
  disabled = false,
  acceptedFileTypes = ".pdf,.doc,.docx,.jpg,.jpeg,.png,image/*",
  onSubmit,
  onFileClick,
  onKeyDown,
  ref,
}: DragDropTextareaProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    triggerFileUpload: () => fileInputRef.current?.click(),
  }));

  function handleDrop(event: React.DragEvent) {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) {
      return;
    }
    const droppedFile = event.dataTransfer?.files?.[0];
    if (droppedFile) {
      onFileChange(droppedFile);
    }
  }

  function handlePaste(event: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (disabled) {
      return;
    }
    const pastedFile = event.clipboardData?.files?.[0];
    if (pastedFile) {
      event.preventDefault();
      onFileChange(pastedFile);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onSubmit?.();
      return;
    }
    onKeyDown?.(event);
  }

  function removeFile() {
    onFileChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop target wrapper, mirrors the Vue component
    <div
      className={cn(
        "relative transition-all duration-200",
        isDragging && "ring-2 ring-indigo-500 ring-offset-2",
        disabled && "cursor-not-allowed opacity-50",
      )}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) {
          setIsDragging(true);
        }
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        setIsDragging(false);
      }}
      onDrop={handleDrop}
    >
      {isDragging ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-indigo-400 bg-indigo-50">
          <div className="text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mx-auto h-12 w-12 text-indigo-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="mt-2 text-sm font-medium text-indigo-600">Déposez votre fichier ici</p>
          </div>
        </div>
      ) : null}

      <textarea
        ref={textareaRef}
        value={value}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          "w-full resize-none border-none p-4 text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6",
          isDragging && "bg-indigo-50",
          disabled && "cursor-not-allowed",
        )}
        onChange={(event) => onValueChange(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />

      {file ? (
        <div className="flex items-center gap-2 px-4 pb-2">
          <div className="inline-flex items-center gap-x-2 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-gray-500/10 ring-inset">
            <FileIcon className="h-3 w-3" />
            <button type="button" onClick={onFileClick} className="cursor-pointer hover:text-gray-900">
              {file.name}
            </button>
            {!disabled ? (
              <button
                type="button"
                onClick={removeFile}
                className="transition-colors hover:text-red-500"
                aria-label="Supprimer le fichier"
              >
                <XIcon className="h-3 w-3" />
              </button>
            ) : null}
          </div>
          <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
        </div>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={acceptedFileTypes}
        disabled={disabled}
        onChange={(event) => {
          const selected = event.target.files?.[0];
          if (selected) {
            onFileChange(selected);
          }
        }}
      />
    </div>
  );
}
